// src/components/coordinateGrid/VectorRenderer.tsx
/**
 * VectorRenderer Component
 * 
 * Renders vector arrows with optional components and labels.
 * Phase 7: Vectors and Inequalities
 */

import React, { useMemo, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../types/redux';
import {
  selectAllVectors,
  selectVectorSums,
  selectVector,
  selectSelectedVectors,
} from '../../store/slices/vectorsSlice';
import { Vector, VectorSum, Point2D } from '../../types/coordinateGrid';

interface VectorRendererProps {
  /** Canvas dimensions */
  width: number;
  height: number;
  /** Viewport transform */
  scaleX: number;
  scaleY: number;
  offsetX: number;
  offsetY: number;
  /** Dark mode */
  isDark?: boolean;
}

/**
 * Convert math coordinates to screen coordinates
 */
function mathToScreen(
  point: Point2D,
  scaleX: number,
  scaleY: number,
  offsetX: number,
  offsetY: number
): { x: number; y: number } {
  return {
    x: offsetX + point.x * scaleX,
    y: offsetY - point.y * scaleY, // Y is inverted in screen coordinates
  };
}

/**
 * Calculate vector magnitude
 */
function calculateMagnitude(start: Point2D, end: Point2D): number {
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  return Math.sqrt(dx * dx + dy * dy);
}

/**
 * Generate arrow marker definition
 */
function ArrowMarker({ id, color }: { id: string; color: string }) {
  return (
    <defs>
      <marker
        id={id}
        markerWidth="10"
        markerHeight="10"
        refX="9"
        refY="3"
        orient="auto"
        markerUnits="strokeWidth"
      >
        <path d="M0,0 L0,6 L9,3 z" fill={color} />
      </marker>
    </defs>
  );
}

export const VectorRenderer: React.FC<VectorRendererProps> = ({
  width,
  height,
  scaleX,
  scaleY,
  offsetX,
  offsetY,
  isDark = false,
}) => {
  const dispatch = useDispatch();
  const vectors = useSelector(selectAllVectors);
  const vectorSums = useSelector(selectVectorSums);
  const selectedVectors = useSelector(selectSelectedVectors);

  // Handle vector selection
  const handleVectorClick = useCallback(
    (id: string, e: React.MouseEvent) => {
      e.stopPropagation();
      dispatch(selectVector({ id, ctrlKey: e.ctrlKey || e.metaKey }));
    },
    [dispatch]
  );

  // Render individual vectors
  const vectorElements = useMemo(() => {
    return vectors.map((vector) => {
      const startScreen = mathToScreen(vector.start, scaleX, scaleY, offsetX, offsetY);
      const endScreen = mathToScreen(vector.end, scaleX, scaleY, offsetX, offsetY);

      const dx = vector.end.x - vector.start.x;
      const dy = vector.end.y - vector.start.y;
      const magnitude = calculateMagnitude(vector.start, vector.end);

      const markerId = `arrow-${vector.id}`;
      const isSelected = vector.isSelected;

      return (
        <g key={vector.id} className={`vector-${vector.id}`}>
          <ArrowMarker id={markerId} color={vector.color} />

          {/* Component lines (dashed horizontal/vertical) */}
          {vector.showComponents && (
            <>
              {/* Horizontal component */}
              <line
                x1={startScreen.x}
                y1={startScreen.y}
                x2={endScreen.x}
                y2={startScreen.y}
                stroke={vector.color}
                strokeWidth={1}
                strokeDasharray="4,4"
                opacity={0.5}
              />
              {/* Vertical component */}
              <line
                x1={endScreen.x}
                y1={startScreen.y}
                x2={endScreen.x}
                y2={endScreen.y}
                stroke={vector.color}
                strokeWidth={1}
                strokeDasharray="4,4"
                opacity={0.5}
              />
              {/* Component labels */}
              <text
                x={startScreen.x + (endScreen.x - startScreen.x) / 2}
                y={startScreen.y - 8}
                fill={vector.color}
                fontSize="12"
                textAnchor="middle"
              >
                {dx >= 0 ? '+' : ''}
                {dx.toFixed(2)}
              </text>
              <text
                x={endScreen.x + 8}
                y={startScreen.y + (endScreen.y - startScreen.y) / 2}
                fill={vector.color}
                fontSize="12"
                dominantBaseline="middle"
              >
                {dy >= 0 ? '+' : ''}
                {dy.toFixed(2)}
              </text>
            </>
          )}

          {/* Main vector arrow */}
          <line
            x1={startScreen.x}
            y1={startScreen.y}
            x2={endScreen.x}
            y2={endScreen.y}
            stroke={vector.color}
            strokeWidth={vector.thickness}
            markerEnd={`url(#${markerId})`}
            opacity={isSelected ? 0.7 : 1}
            onClick={(e) => handleVectorClick(vector.id, e)}
            style={{ cursor: 'pointer' }}
          />

          {/* Selection highlight */}
          {isSelected && (
            <line
              x1={startScreen.x}
              y1={startScreen.y}
              x2={endScreen.x}
              y2={endScreen.y}
              stroke={isDark ? '#60A5FA' : '#2563EB'}
              strokeWidth={vector.thickness + 4}
              opacity={0.3}
              pointerEvents="none"
            />
          )}

          {/* Magnitude label */}
          {vector.showMagnitude && (
            <text
              x={startScreen.x + (endScreen.x - startScreen.x) / 2}
              y={startScreen.y + (endScreen.y - startScreen.y) / 2 - 15}
              fill={vector.color}
              fontSize="12"
              textAnchor="middle"
              fontWeight="bold"
            >
              |v| = {magnitude.toFixed(2)}
            </text>
          )}

          {/* Vector label */}
          {vector.label && (
            <text
              x={endScreen.x + 10}
              y={endScreen.y - 10}
              fill={vector.color}
              fontSize="14"
              fontWeight="bold"
            >
              {vector.label}
            </text>
          )}

          {/* Column vector notation */}
          {vector.showComponents && (
            <g transform={`translate(${endScreen.x + 20}, ${endScreen.y - 20})`}>
              <text
                fill={vector.color}
                fontSize="14"
                fontFamily="serif"
                textAnchor="middle"
              >
                [
              </text>
              <text
                y="12"
                fill={vector.color}
                fontSize="12"
                textAnchor="middle"
              >
                {dx.toFixed(2)}
              </text>
              <text
                y="24"
                fill={vector.color}
                fontSize="12"
                textAnchor="middle"
              >
                {dy.toFixed(2)}
              </text>
              <text
                y="36"
                fill={vector.color}
                fontSize="14"
                fontFamily="serif"
                textAnchor="middle"
              >
                ]
              </text>
            </g>
          )}
        </g>
      );
    });
  }, [vectors, scaleX, scaleY, offsetX, offsetY, isDark, handleVectorClick]);

  // Render vector sums (resultants)
  const vectorSumElements = useMemo(() => {
    return vectorSums.map((sum) => {
      const startScreen = mathToScreen(
        sum.resultant.start,
        scaleX,
        scaleY,
        offsetX,
        offsetY
      );
      const endScreen = mathToScreen(
        sum.resultant.end,
        scaleX,
        scaleY,
        offsetX,
        offsetY
      );

      const markerId = `arrow-sum-${sum.id}`;

      return (
        <g key={sum.id} className={`vector-sum-${sum.id}`}>
          <ArrowMarker id={markerId} color={sum.resultantColor} />

          {/* Show component vectors if enabled */}
          {sum.showComponents &&
            sum.vectorIds.map((vectorId) => {
              const vector = vectors.find((v) => v.id === vectorId);
              if (!vector) return null;

              const vStartScreen = mathToScreen(
                vector.start,
                scaleX,
                scaleY,
                offsetX,
                offsetY
              );
              const vEndScreen = mathToScreen(
                vector.end,
                scaleX,
                scaleY,
                offsetX,
                offsetY
              );

              return (
                <line
                  key={vectorId}
                  x1={vStartScreen.x}
                  y1={vStartScreen.y}
                  x2={vEndScreen.x}
                  y2={vEndScreen.y}
                  stroke={vector.color}
                  strokeWidth={vector.thickness}
                  strokeDasharray="2,2"
                  opacity={0.5}
                />
              );
            })}

          {/* Resultant vector */}
          <line
            x1={startScreen.x}
            y1={startScreen.y}
            x2={endScreen.x}
            y2={endScreen.y}
            stroke={sum.resultantColor}
            strokeWidth={4}
            markerEnd={`url(#${markerId})`}
            opacity={0.8}
          />

          {/* Resultant label */}
          <text
            x={endScreen.x + 10}
            y={endScreen.y - 10}
            fill={sum.resultantColor}
            fontSize="14"
            fontWeight="bold"
          >
            R
          </text>
        </g>
      );
    });
  }, [vectorSums, vectors, scaleX, scaleY, offsetX, offsetY]);

  return (
    <g className="vector-layer">
      {vectorElements}
      {vectorSumElements}
    </g>
  );
};
